import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listChatMessages, sendChatMessage, type MessagePage, type StreamMessage } from './messagesApi';

const LIMIT = 50;

export function messageQueryKey(chatId: string) {
  return ['chat-messages', chatId] as const;
}

export function flattenMessagePages(data: { pages: MessagePage[] } | undefined): StreamMessage[] {
  return data?.pages.flatMap((p) => p.messages) ?? [];
}

export function useMessages(chatId: string) {
  return useInfiniteQuery({
    queryKey: messageQueryKey(chatId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listChatMessages(chatId, { since: pageParam, limit: LIMIT }),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Boolean(chatId),
  });
}

export function useSendMessage(chatId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => sendChatMessage(chatId, { body }),
    onMutate: async (body: string) => {
      await qc.cancelQueries({ queryKey: messageQueryKey(chatId) });
      const previous = qc.getQueryData<{ pages: MessagePage[]; pageParams: unknown[] }>(messageQueryKey(chatId));
      const optimistic: StreamMessage = {
        id: `optimistic-${Date.now()}`,
        chat_id: chatId,
        sender: 'user',
        body,
        attachments: [],
        metadata: {},
        created_at: new Date().toISOString(),
        optimistic: true,
      };
      qc.setQueryData(messageQueryKey(chatId), (old: { pages: MessagePage[]; pageParams: unknown[] } | undefined) => {
        if (!old) return { pages: [{ messages: [optimistic], next_cursor: null }], pageParams: [undefined] };
        const pages = old.pages.length ? [...old.pages] : [{ messages: [], next_cursor: null }];
        const last = pages[pages.length - 1];
        pages[pages.length - 1] = { ...last, messages: [...last.messages, optimistic] };
        return { ...old, pages };
      });
      return { previous, optimisticId: optimistic.id };
    },
    onSuccess: (created, _body, ctx) => {
      qc.setQueryData(messageQueryKey(chatId), (old: { pages: MessagePage[]; pageParams: unknown[] } | undefined) => {
        if (!old) return { pages: [{ messages: [created], next_cursor: null }], pageParams: [undefined] };
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((m) => m.id === ctx?.optimisticId ? created : m),
          })),
        };
      });
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.previous) qc.setQueryData(messageQueryKey(chatId), ctx.previous);
    },
  });
}
